import { Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useHasRole } from '@/shared/hooks/useRole';
import { Breadcrumb, Button, DataTable, PageHeader, SelectMenu } from '@/shared/ui';

import { createOrderColumns } from '../components/orderColumns';
import { OrderCancelReturnDialog } from '../components/OrderCancelReturnDialog';
import { OrderFiltersBar } from '../components/OrderFiltersBar';
import { OrderStatsSection } from '../components/OrderStatsSection';
import { useContinuePendingPaymentAction } from '../hooks/useContinuePendingPaymentAction';
import { useOrderManagementState } from '../hooks/useOrderManagementState';
import type { OrderCancelReturnAction, OrderRow } from '../types';

export function OrderManagementPage() {
  const { t: tNav } = useTranslation('common');
  const navigate = useNavigate();
  const list = useOrderManagementState();
  const { t, originOptions } = list;
  const canContinuePendingPayment = useHasRole('admin');
  const { continueOrder, continuingOrderId } = useContinuePendingPaymentAction();
  const [dialog, setDialog] = useState<{
    orderId: string;
    action: OrderCancelReturnAction;
  } | null>(null);

  const onAction = useCallback((row: OrderRow, action: OrderCancelReturnAction) => {
    setDialog({ orderId: row.id, action });
  }, []);

  const onContinue = useCallback(
    (row: OrderRow) => {
      void continueOrder({ id: row.id, orderNumber: row.orderNumber });
    },
    [continueOrder],
  );

  const columns = useMemo(
    () =>
      createOrderColumns({
        t,
        onAction,
        onContinue,
        continuingOrderId,
        canContinuePendingPayment,
      }),
    [t, onAction, onContinue, continuingOrderId, canContinuePendingPayment],
  );

  const warehouseOptions = useMemo(
    () => [{ value: 'all', label: t('filters.allWarehouses') }, ...originOptions],
    [originOptions, t],
  );

  const onRowClick = useCallback(
    (row: OrderRow) => {
      void navigate(`/orders/${encodeURIComponent(row.id)}`);
    },
    [navigate],
  );

  return (
    <div
      data-page="orders/index"
      className="mx-auto flex min-h-full w-full flex-col gap-4"
    >
      <Breadcrumb
        items={[
          { label: tNav('nav.home' as Parameters<typeof tNav>[0]), to: '/home' },
          { label: tNav('nav.orders' as Parameters<typeof tNav>[0]), current: true },
        ]}
      />
      <PageHeader
        title={list.t('title')}
        description={list.t('subtitle')}
        actions={
          <>
            <SelectMenu
              value={list.originId}
              onChange={list.onOriginChange}
              options={warehouseOptions}
              placeholder={list.t('filters.allWarehouses')}
              emptyLabel={list.t('filters.emptyWarehouses')}
              className="h-10 min-w-[180px] rounded-md"
            />
            <Button type="button" variant="primary" size="md" className="gap-2">
              <Plus aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
              {list.t('actions.create')}
            </Button>
          </>
        }
      />

      <OrderStatsSection stats={list.stats} t={list.t} isLoading={list.statsLoading} />

      <OrderFiltersBar
        t={list.t}
        query={list.query}
        onQueryChange={list.onQueryChange}
        statusFilter={list.statusFilter}
        statusOptions={list.statusOptions}
        onStatusFilterChange={list.onStatusFilterChange}
        sortBy={list.sortBy}
        sortOptions={list.sortOptions}
        onSortByChange={list.onSortByChange}
      />

      <DataTable
        tableId="orders-management"
        columns={columns}
        data={list.paged}
        isLoading={list.listLoading || (list.listFetching && list.paged.length === 0)}
        isError={list.listError}
        errorMessage={list.listErrorMessage}
        showColumnControls={false}
        sorting={list.sorting}
        onSortingChange={list.setSorting}
        pageIndex={list.pageIndex}
        pageSize={list.pageSize}
        pageCount={list.pageCount}
        totalRows={list.totalRows}
        onPageChange={list.setPageIndex}
        onPageSizeChange={list.setPageSize}
        onRowClick={onRowClick}
        caption={list.t('table.caption')}
      />

      <OrderCancelReturnDialog
        open={Boolean(dialog)}
        orderId={dialog?.orderId ?? null}
        action={dialog?.action ?? null}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
      />
    </div>
  );
}
